import React, { useEffect, useMemo, useState } from 'react'
import swal from 'sweetalert2';
import { getTournamentHoleSelection, saveTournamentHoleSelection } from '../api/tournamentAPI';
// import { getHoleChecks, saveHoleChecks } from '../../api/tournamentAPI';
// NOTE: add these 2 functions to tournamentAPI.js (uses your existing axios instance):
//   payload shape -> { TournamentDrawID, Selections: [{ Hole, PlayerID, PlayerType }, ...] }

const TOTAL_HOLES = 18;
const MAX_PLAYERS_PER_HOLE = 3;
const MAX_HOLES_PER_PLAYER = 4;

const buildEmptyChecks = () => {
    const state = {};
    for (let hole = 1; hole <= TOTAL_HOLES; hole++) {
        state[hole] = { 1: false, 2: false, 3: false, 4: false };
    }
    return state;
};

const TournamentHolePlayerCheck = ({ tournamentDraw, selectedPlayer, closeDrawer }) => {

    const [checks,      setChecks]      = useState(buildEmptyChecks());
    const [savedChecks, setSavedChecks] = useState(buildEmptyChecks()); // last-saved snapshot, for dirty-check
    const [loading,     setLoading]     = useState(true);
    const [saving,      setSaving]      = useState(false);

    const players = useMemo(() => ([1, 2, 3, 4].map(n => ({
        playerNo   : n,
        playerID   : selectedPlayer[`Player${n}ID`],
        playerName : selectedPlayer[`Player${n}Nick`],
        playerType : selectedPlayer[`Player${n}Type`]
    }))), [selectedPlayer]);

    // console.log('TournamentHolePlayerCheck render', selectedPlayer); // tournamentDraw, selectedPlayer,

    // useEffect(() => {
    //     console.log('TournamentHolePlayerCheck mounted', checks); // tournamentDraw, selectedPlayer,
    // }, [tournamentDraw, selectedPlayer, checks, savedChecks]);

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            try {
                const HoleSelection = await getTournamentHoleSelection(tournamentDraw.ID);
                const hydrated = buildEmptyChecks();
                (HoleSelection || []).forEach(row => {
                    if (hydrated[row.Hole]) hydrated[row.Hole][row.PlayerNo] = true;
                });
                setChecks(hydrated);
                setSavedChecks(hydrated);
            } catch (err) {
                swal.fire({ icon: 'error', titleText: 'Error!', text: 'Could not load selections' });
            } finally {
                setLoading(false);
            }
        };
        if (tournamentDraw?.ID) load();
    }, [tournamentDraw]);

    // -- derived counts -----------------------------------------------
    const rowCount = (hole) => Object.values(checks[hole] || {}).filter(Boolean).length;

    const colCount = (playerType) => {
        let count = 0;
        for (let hole = 1; hole <= TOTAL_HOLES; hole++) {
            if (checks[hole]?.[playerType]) count++;
        }
        return count;
    };

    const isDirty = useMemo(
        () => JSON.stringify(checks) !== JSON.stringify(savedChecks),
        [checks, savedChecks]
    );

    // -- toggle (local only, no API call) --------------------------------
    const handleToggle = (hole, player) => {
        const isCurrentlyChecked = checks[hole][player.playerNo];

        if (!isCurrentlyChecked) {
            if (rowCount(hole) >= MAX_PLAYERS_PER_HOLE) {
                swal.fire({ icon: 'warning', titleText: 'Limit Reached', text: `Only ${MAX_PLAYERS_PER_HOLE} players can be checked per hole.` });
                return;
            }
            if (colCount(player.playerNo) >= MAX_HOLES_PER_PLAYER) {
                swal.fire({ icon: 'warning', titleText: 'Limit Reached', text: `${player.playerName} is already checked for the maximum of ${MAX_HOLES_PER_PLAYER} holes.` });
                return;
            }
        }

        setChecks(prev => ({
            ...prev,
            [hole]: { ...prev[hole], [player.playerNo]: !isCurrentlyChecked }
        }));
    };

    // -- delete all (local clear, takes effect on Save) --------------------
    const handleDeleteAll = () => {
        swal.fire({
            icon              : 'warning',
            titleText         : 'Clear all selections?',
            text              : 'This clears the grid — click Save to make it permanent.',
            showCancelButton  : true,
            confirmButtonText : 'Yes, clear all',
        }).then((result) => {
            if (result.isConfirmed) setChecks(buildEmptyChecks());
        });
    };

    // -- save everything at once -------------------------------------------
    const handleSave = async () => {
        const selections = [];
        for (let hole = 1; hole <= TOTAL_HOLES; hole++) {
            players.forEach(p => {
                if (checks[hole][p.playerNo]) {
                    selections.push({
                        Hole: hole, PlayerNo : p.playerNo, PlayerID: p.playerID, PlayerName: p.playerName, PlayerType: p.playerType
                    });
                }
            });
        }

        // console.log('handleSave', selections);

        setSaving(true);
        try {
            const data = await saveTournamentHoleSelection({
                tournamentDrawID : tournamentDraw.ID,
                selections       : selections
            });
            if (!data?.Message == 'success') throw new Error(data?.Message || 'Could not save');

            setSavedChecks(checks);
            swal.fire({ icon: 'success', titleText: 'Saved', timer: 1200, showConfirmButton: false, animation: false });
        } catch (err) {
            swal.fire({ icon: 'error', titleText: 'Error!', text: err.message || 'Something went wrong' });
        } finally {
            setSaving(false);
        }
    };

    const handleClose = () => {
        if (!isDirty) return closeDrawer();
        swal.fire({
            icon              : 'warning',
            titleText         : 'Discard unsaved changes?',
            showCancelButton  : true,
            confirmButtonText : 'Discard',
        }).then(result => { if (result.isConfirmed) closeDrawer(); });
    };

    const handleDeselectAll = () => {
        setChecks(buildEmptyChecks());
    };

    if (loading) return <div className="text-center p-4">Loading...</div>;

    return (
        <div className="hole-check-panel">
            <div
                className="hole-check-scroll border"
                style={{ maxHeight: 500, overflowY: 'auto', borderRadius: 4 }}
            >
                <table className="table table-sm table-hover mb-0" style={{ fontSize: 12 }}>
                    <thead>
                        <tr style={{ position: 'sticky', top: 0, background: '#f7f8fa', zIndex: 1 }}>
                            <th className="border text-center" style={{ textAlign: 'center', width: '10%', padding: '6px 4px' }}>Hole</th>
                            {players.map(p => {
                                const count = colCount(p.playerNo);
                                const atMax = count >= MAX_HOLES_PER_PLAYER;
                                return (
                                    <th className="border text-center" key={p.playerNo} style={{ textAlign: 'center', padding: '6px 4px' }}>
                                        <div className="text-clip">{p.playerName}</div>
                                        <span className={`badge ${atMax ? 'badge-success' : 'badge-light'}`} style={{ fontWeight: 400 }}>
                                            {count}/{MAX_HOLES_PER_PLAYER}
                                        </span>
                                    </th>
                                );
                            })}
                            <th className="border text-center" style={{ textAlign: 'center', width: '10%', padding: '6px 4px' }}>Sel.</th>
                        </tr>
                    </thead>
                    <tbody>
                        {Array.from({ length: TOTAL_HOLES }, (_, i) => i + 1).map(hole => {
                            const holeCount = rowCount(hole);
                            const holeAtMax = holeCount >= MAX_PLAYERS_PER_HOLE;
                            return (
                                <tr key={hole}>
                                    <td className="border text-center" style={{ textAlign: 'center', padding: '4px' }}>{hole}</td>
                                    {players.map(p => {
                                        const checked = checks[hole][p.playerNo];
                                        const disable = !checked && (
                                            holeAtMax || colCount(p.playerNo) >= MAX_HOLES_PER_PLAYER
                                        );
                                        return (
                                            // <td key={p.playerNo} className={`border hole-cell ${disable && "disabled"}`} style={{ textAlign: 'center', padding: '4px' }} onClick={() => {
                                            //         if (!disable) {
                                            //             handleToggle(hole, p);
                                            //         }
                                            //     }}>
                                            //     <label className="kt-checkbox kt-checkbox--bold kt-checkbox--success">
                                            //         <input
                                            //             type="checkbox"
                                            //             checked={checked}
                                            //             disabled={disable}
                                            //             onChange={() => handleToggle(hole, p)}
                                            //         />
                                            //         <span></span>
                                            //     </label>
                                            // </td>
                                            <td key={p.playerNo} className="border hole-cell" style={{ textAlign: 'center', padding: '4px' }}>
                                                <label className="kt-checkbox kt-checkbox--bold kt-checkbox--success">
                                                    <input
                                                        type="checkbox"
                                                        checked={checked}
                                                        disabled={disable}
                                                        onChange={() => handleToggle(hole, p)}
                                                    />
                                                    <span></span>
                                                </label>
                                            </td>
                                            // <td
                                            //     key={p.playerNo}
                                            //     className={`
                                            //         hole-cell
                                            //         border
                                            //         text-center
                                            //         ${checked ? "selected" : ""}
                                            //         ${disable ? "disabled" : ""}
                                            //     `}
                                            //     onClick={() => {
                                            //         if (!disable) {
                                            //             handleToggle(hole, p);
                                            //         }
                                            //     }}
                                            // >
                                            //     <label
                                            //         className="kt-checkbox kt-checkbox--bold kt-checkbox--success mb-0"
                                            //         style={{
                                            //             display: "flex",
                                            //             justifyContent: "center",
                                            //             alignItems: "center",
                                            //             pointerEvents: "none"
                                            //         }}
                                            //     >
                                            //         <input
                                            //             type="checkbox"
                                            //             checked={checked}
                                            //             readOnly
                                            //             tabIndex={-1}
                                            //         />
                                            //         <span></span>
                                            //     </label>
                                            // </td>
                                        );
                                    })}
                                    <td className="border" style={{ textAlign: 'center', padding: '4px' }}>
                                        <span className={`badge ${holeAtMax ? 'badge-success' : 'badge-light'}`} style={{ fontWeight: 400 }}>
                                            {holeCount}/{MAX_PLAYERS_PER_HOLE}
                                        </span>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            <div className="d-flex align-items-center justify-content-between mt-2">
                <small className="text-muted">{isDirty ? 'Unsaved changes' : 'All changes saved'}</small>
                <div>
                    <button type="button" className="btn btn-info btn-sm mr-2" onClick={handleDeselectAll} disabled={saving}>
                        Deselect All
                    </button>
                    {/* <button type="button" className="btn btn-danger btn-sm mr-2" onClick={handleDeleteAll} disabled={saving}>
                        Delete All
                    </button> */}
                    <button type="button" className="btn btn-success btn-sm mr-2" onClick={handleSave} disabled={saving || !isDirty}>
                        {saving ? 'Saving...' : 'Save'}
                    </button>
                    <button type="button" className="btn btn-secondary btn-sm" onClick={handleClose} disabled={saving}>
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

export default TournamentHolePlayerCheck;